/**
 * O CSV que o painel entrega, num lugar só.
 *
 * Nasceu em `lib/adminPos.ts` (a planilha das matrículas, 000206) e saiu de lá
 * em 29/09, quando o painel das avaliações passou a exportar também: uma
 * segunda cópia do escape seria a segunda definição de "célula segura", e a
 * que divergisse em silêncio seria a que ninguém revê.
 *
 * ---------------------------------------------------------------------------
 * CSV, e não .xlsx
 * ---------------------------------------------------------------------------
 * "Exportar planilha" foi o pedido, e planilha é o que o Excel abre. Gerar
 * `.xlsx` de verdade exigiria `exceljs` — **939 KB, 270 KB comprimido**, para
 * botões que a equipe usa algumas vezes por mês. O hub inteiro é servido em um
 * bundle; pagar isso em toda visita de aluno para um recurso de operador é o
 * mesmo raciocínio que fez o currículo virar impressão do navegador.
 *
 * O que faz o CSV abrir certo no Excel em português:
 *   · **BOM UTF-8** (`﻿`) no começo — sem ele o Excel lê como ANSI e todo
 *     acento vira `Ã§`. É a razão nº 1 de "a planilha veio com caracteres
 *     estranhos".
 *   · **ponto e vírgula** como separador — é o que o Excel pt-BR espera, porque
 *     a vírgula é o separador DECIMAL aqui. Pelo mesmo motivo os números
 *     decimais vão com vírgula (`numeroParaCsv`).
 *   · a linha `sep=;` antes do cabeçalho, que o Excel lê como instrução.
 */

/**
 * O NÚMERO PURO, como `numeroParaCsv` o escreve: o sinal de menos opcional,
 * dígitos e a vírgula decimal opcional ("-0,49", "12", "-67"). Sem `+`: o Excel
 * lê "+5" como fórmula ("=+5"), e `numeroParaCsv` nunca escreve o sinal de mais.
 */
const NUMERO_PURO = /^-?\d+(,\d+)?$/;

/**
 * Escapa uma célula de CSV.
 *
 * As aspas duplicadas e o envelope quando há `;`, `"` ou quebra de linha — nome
 * de aluno com vírgula existe, e sem isto ele parte a linha em duas colunas.
 *
 * E o `'` na frente de valor que começa com `=`, `+`, `-` ou `@`: o Excel avalia
 * essas células como FÓRMULA (a injeção de CSV), e um nome que começa com `=`
 * viraria erro na planilha da secretaria — ou pior, uma fórmula que roda. O
 * comentário de uma avaliação é texto livre de aluno: é exatamente o caso.
 *
 * Menos no NÚMERO PURO (desde 29/09): número não é fórmula, e o Excel o lê como
 * número. Com o apóstrofo, toda variação negativa virava TEXTO na planilha por
 * ferramenta da Visão geral — nos 30 dias de 29/09, 7 das 12 ferramentas, que
 * são justamente as que caíram — e texto não soma nem ordena. O resto do escape
 * fica como estava. Na planilha das matrículas o efeito é um caso só: as
 * colunas de número dela são contagens, que não começam por `-`, a não ser
 * "Meses desde o início" de uma matrícula que ainda vai começar (a conta é
 * `age(current_date, inicio)`, e dá negativo) — e essa passa a sair como número,
 * que é o que ela é.
 */
export function celulaDeCsv(v: unknown): string {
  const s = String(v ?? "");
  const seguro = /^[=+\-@\t\r]/.test(s) && !NUMERO_PURO.test(s) ? `'${s}` : s;
  return /[";\r\n]/.test(seguro) ? `"${seguro.replace(/"/g, '""')}"` : seguro;
}

/** O arquivo inteiro: BOM, `sep=;`, cabeçalho e corpo, com CRLF. */
export function montarCsv(cabecalho: string[], corpo: unknown[][]): string {
  return (
    "﻿" +
    "sep=;\r\n" +
    [cabecalho, ...corpo].map((linha) => linha.map(celulaDeCsv).join(";")).join("\r\n") +
    "\r\n"
  );
}

/**
 * Número com a vírgula decimal do Excel pt-BR, sem separador de milhar (o
 * ponto de milhar faria o Excel ler "1.234" como um vírgula dois três quatro).
 * Nulo vira célula vazia: "não há número" não é zero.
 */
export function numeroParaCsv(v: number | null | undefined, casas = 2): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  return v.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas, useGrouping: false });
}

/** Entrega o arquivo ao navegador. Revoga a URL — blob não coletado é vazamento. */
export function baixarCsv(csv: string, nome: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
